import React, { useCallback, useEffect, useState } from 'react';
import ConsultationsPanel, { useConsultationThreads } from './ConsultationsPanel';
import CommentsRail from './CommentsRail';
import { OPEN_COMMENT_EVENT } from './openComment';
import { ReviewComment } from './types';

/**
 * The viewer's right rail (2026-08-26, Fredrik).
 *
 * WHY THIS EXISTS
 *
 * Consultations, and then the comment list, were rendered by the PARENT views
 * (ReviewInterface, PreScreenView, DecisionView) as sections BELOW the viewer,
 * so answering a question or reading a finding meant scrolling past the entire
 * document — and once you were there, you could no longer see the content it
 * was about. The one surface in this layout that stays put while the document
 * scrolls is the viewer's sticky right column, so that is where they belong.
 * Nothing about a cycle's comments is rendered below the content any more.
 *
 * Before this, the rail was not a container at all: CommentComposer rendered
 * `.viewer-comment-form` directly as the flex sibling, which meant the right
 * column simply did not exist unless a comment was being composed. This owns
 * the column now; the composer is passed in as a child and the CSS strips its
 * standalone sticky/width so it reads as panel content.
 *
 * BOTH VIEWERS RENDER THIS. DocumentViewer delegates to SnapshotViewer for
 * webpages, and the two have drifted repeatedly — an overlay or form change
 * made in DocumentViewer alone has shipped dead for web cycles more than
 * once. One component, rendered twice, is the only shape that cannot drift.
 *
 * DENSITY IS THE WHOLE DESIGN. Everything a cycle has to say now competes for
 * one 360px column, so: the rail collapses entirely (the content takes the
 * width back), rows start collapsed and clip their quote and body to a fixed
 * number of characters, and one row is open at a time. A reader expands what
 * they are actually reading and the rest stays scannable.
 *
 * MOBILE (<=768px): the rail unsticks and stacks. CSS gives it `order: -1` so
 * it sits ABOVE the document rather than below — otherwise the fix does not
 * apply on a phone at all, which is the exact complaint that started this.
 * It also starts COLLAPSED there, so it costs a header bar rather than a
 * screenful before the content.
 */

interface ViewerSidePanelProps {
  cycleId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  /** Bumped by the parent on every refetch — REQUIRED, not optional: a
      fetch-once panel that never re-reads is the dominant bug shape here. */
  refreshKey: string | number;
  viewerEmail?: string;
  canEditAny?: boolean;
  /** The composer element, or null when nothing is selected to comment on. */
  composer?: React.ReactNode;
  /** Rendered above the tabs — submit errors belong to the whole rail. */
  banner?: React.ReactNode;
  /** Findings for the ACTIVE tab of the viewer, already filtered by the host's
      visibility toggle. Roots only — a reply belongs to its thread. */
  comments?: ReviewComment[];
  /** What a comment row expands into. Supplied by the host so each view keeps
      its own card: reviewer, decision maker, read-only. */
  renderCommentDetail?: (comment: ReviewComment) => React.ReactNode;
  /** Counters and bulk actions for the comments tab (the DM's undecided count
      and "Apply all remaining" live here). */
  commentsHeader?: React.ReactNode;
}

type PanelTab = 'comment' | 'comments' | 'threads';

const COLLAPSE_KEY = 'cellcohub.viewerRail.collapsed';

/** Collapsed by default on narrow screens, and whatever the reader last chose
    everywhere else. Read once: a resize mid-review should not yank the panel
    shut under someone's cursor. */
function initiallyCollapsed(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px)').matches) {
      return true;
    }
    return window.localStorage?.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;   // never let storage or a media query hide the panel
  }
}

const ViewerSidePanel: React.FC<ViewerSidePanelProps> = ({
  cycleId, getAuthHeaders, refreshKey, viewerEmail, canEditAny = false, composer, banner,
  comments = [], renderCommentDetail, commentsHeader
}) => {
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const [tab, setTab] = useState<PanelTab>('comments');
  const [openId, setOpenId] = useState<string | null>(null);

  const showComments = !!renderCommentDetail;

  const setCollapsedPersisted = useCallback((next: boolean) => {
    setCollapsed(next);
    try { window.localStorage?.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* private mode */ }
  }, []);

  // The composer appears when a selection is made, and that IS the user asking
  // to write — so it takes the rail. Without this, selecting text while
  // another tab is open silently does nothing visible.
  const [lastComposer, setLastComposer] = useState<boolean>(!!composer);
  if (!!composer !== lastComposer) {
    setLastComposer(!!composer);
    if (composer) { setTab('comment'); setCollapsed(false); }
    else if (tab === 'comment') setTab(showComments ? 'comments' : 'threads');
  }

  // A marker click in the content. The card it wants is in this panel, which
  // may be collapsed or on another tab — so open to the right place first.
  // Which tab: findings are in `comments`, and anything else is a
  // consultation, which is the only other thing the rail lists.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const id = (e as CustomEvent<{ commentId?: string }>).detail?.commentId;
      if (!id) return;
      setCollapsed(false);
      setTab(showComments && comments.some(c => c._id === id) ? 'comments' : 'threads');
      setOpenId(id);
    };
    window.addEventListener(OPEN_COMMENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_COMMENT_EVENT, onOpen);
  }, [comments, showComments]);

  const activate = useCallback((next: PanelTab) => {
    setTab(next);
    setCollapsedPersisted(false);
  }, [setCollapsedPersisted]);

  const showComposer = tab === 'comment' && !!composer;
  const unresolved = comments.filter(c => !c.resolved).length;

  // The consultations fetch lives HERE, not in the panel it feeds (2026-08-28,
  // Fredrik). The panel is rendered in a ternary against the comments rail and
  // is not mounted at all while the rail is collapsed, so a count it owned
  // could not reach the tab that has to show it — which is why Consultations
  // was the one tab with no number. Fredrik asked for the TOTAL, not the open
  // count: the tab answers "is there anything here", and the panel's own
  // header already breaks it down as `N open · M answered`.
  const { threads, loadError: threadsError, refetch: refetchThreads } =
    useConsultationThreads(cycleId, getAuthHeaders, refreshKey);
  const consultationCount = (threads || []).length;

  const tabButton = (key: PanelTab, label: string, count?: number) => (
    <button
      role="tab"
      aria-selected={tab === key && !collapsed}
      className={`viewer-side-panel-tab${tab === key && !collapsed ? ' is-active' : ''}`}
      onClick={() => activate(key)}
      data-testid={`side-panel-tab-${key}`}
    >
      {label}
      {count !== undefined && count > 0 && <span className="viewer-side-panel-tab-count">{count}</span>}
    </button>
  );

  return (
    <aside
      className={`viewer-side-panel${collapsed ? ' is-collapsed' : ''}`}
      data-testid="viewer-side-panel"
    >
      {banner}
      <div className="viewer-side-panel-tabs">
        {/* role="tablist" belongs on the tabs ALONE (2026-08-26). It sat on
            this outer row, which also holds the collapse button and the
            collapsed count — and a tablist whose children are not all tabs is
            a critical axe violation (aria-required-children). It went unseen
            because no scan had ever opened this rail: the suite's other axe
            tests use a cycle with no content, which renders no viewer and so
            no panel at all. */}
        <div className="viewer-side-panel-tablist" role="tablist">
          {composer && tabButton('comment', 'Comment')}
          {showComments && tabButton('comments', 'Comments', unresolved)}
          {tabButton('threads', 'Consultations', consultationCount)}
        </div>
        {/* The whole rail, out of the way. On a snapshot cycle the content
            does not reflow when this happens — the stage is pinned to the
            capture width and scaled — so markers stay where they were placed. */}
        <button
          type="button"
          className="viewer-side-panel-collapse"
          onClick={() => setCollapsedPersisted(!collapsed)}
          aria-expanded={!collapsed}
          data-testid="side-panel-collapse"
          title={collapsed ? 'Show comments' : 'Hide the panel and give the content the space'}
        >
          {collapsed ? '«' : '»'}
        </button>
        {/* Collapsed, the rail is a 44px strip — the one thing worth the space
            is whether anything is still waiting. */}
        {collapsed && unresolved > 0 && (
          <span className="viewer-side-panel-collapsed-count" title={`${unresolved} unresolved`}>
            {unresolved}
          </span>
        )}
      </div>

      {!collapsed && (
        <div className="viewer-side-panel-body">
          {showComposer ? composer : tab === 'comments' && showComments ? (
            <CommentsRail
              comments={comments}
              viewerEmail={viewerEmail}
              renderDetail={renderCommentDetail!}
              header={commentsHeader}
              openId={openId}
              onOpenIdHandled={() => setOpenId(null)}
              emptyText="No comments on this content yet."
            />
          ) : (
            <ConsultationsPanel
              cycleId={cycleId}
              getAuthHeaders={getAuthHeaders}
              threads={threads}
              loadError={threadsError}
              refetch={refetchThreads}
              viewerEmail={viewerEmail}
              canEditAny={canEditAny}
              openId={openId}
              onOpenIdHandled={() => setOpenId(null)}
            />
          )}
        </div>
      )}
    </aside>
  );
};

export default ViewerSidePanel;
